// Core Modules
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

// External Modules
import { BehaviorSubject, catchError, filter, Observable, retry, switchMap, take, throwError, timer } from 'rxjs';

// Services
import { AuthService } from '@core/services/auth/auth.service';
import { ConfigService } from '@core/services/config/config.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';

// Models
import { AppEndpointsModel } from '@shared/models/endpoints.config.model';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { ErrorHandlerCodesEnum } from '@shared/enum/error-handler-codes.enum';

/**
 * Auth Interceptor
 */
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor {
  // Injectors
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly router = inject(Router);
  private readonly sessionManagerService = inject(SessionManagerService);

  // Private Members
  /**
   * appEndpoints to get app endpoints
   */
  private appEndpoints: AppEndpointsModel;
  /**
   * authContents to get auth contents
   */
  private authContents: any;
  /**
   * refreshTokenInProgress to get boolean: true/false
   */
  private refreshTokenInProgress = false;
  /**
   * refresh token handler
   */
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  /**
   * holds whitelisted endpoint
   */
  private whiteListUrls: string[] = [];

  constructor() {
    this.appEndpoints = this.configService.getEndpointsByModule('appEndpoints');
    this.authContents = this.configService.getContents('auth');
    // build whitelisted endpoints
    this.whiteListUrls = [
      `${this.configService.getAPIBaseUrl}${this.appEndpoints.legacyFederatedDomain}`,
      `${this.configService.getAPIBaseUrl}${this.appEndpoints.clientList}?products=CRM`,
      `${this.configService.getAPIBaseUrl}${this.appEndpoints.sessionRefresh}`,
    ];
  }

  /**
   * method to handle response error
   * @param error error
   * @param request http request
   * @param next http handler
   * @returns error handler
   */
  private handleResponseError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    // logout if /refresh, auth/me or auth/me/clients api fail
    if ((request.url.includes(`${this.appEndpoints.sessionRefresh}`) || request.url.includes(`${this.appEndpoints.userScopes}`)) && !request.url.includes(`${this.appEndpoints.userScopes}/tokens`)) {
      this.sessionManagerService.stopWatching();
      this.authService.logout();
      return this.errorHandlerService.errorHandler(error);
    }

    if (error instanceof HttpErrorResponse) {
      // Invalid token error
      if (error.status === ErrorHandlerCodesEnum.UNAUTHORIZED || error.status === ErrorHandlerCodesEnum.SERVICE_UNAVAILABLE) {
        return this.reAuthenticationHandler(error, request, next);
      }

      // Redirect to error page
      if (error.status === ErrorHandlerCodesEnum.FORBIDDEN) {
        this.router.navigate([`/${MainNavigationEnum.UNAUTHORIZED}`]);
      }
    }

    return this.errorHandlerService.errorHandler(error);
  }

  /**
   * method to navigate to login
   */
  private navigateToLogin(): void {
    this.authService
      .isSessionValid()
      .pipe(take(1))
      .subscribe((currentSessionStatus: boolean) => {
        console.log(`<AuthInterceptor> - <navigateToLogin> - Current status value is: ${currentSessionStatus}`);
        if (!currentSessionStatus) {
          this.sessionManagerService.stopWatching();
          this.authService.logout();
        }
      });
  }

  /**
   * method to re-authentication handler
   * @param error error
   * @param request http request
   * @param next http handler
   * @returns refresh token
   */
  private reAuthenticationHandler(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    // `isSessionValid()` returns an Observable — it must be resolved before deciding
    // whether to refresh the token or log the user out (previously it was tested for
    // truthiness directly, which is always true and skipped the logout path).
    return this.authService.isSessionValid().pipe(
      take(1),
      switchMap((isSessionValid: boolean) => {
        if (isSessionValid) {
          // call refresh token API and retry
          return this.refreshToken(request, next);
        }
        this.sessionManagerService.stopWatching();
        this.authService.logout();
        return this.errorHandlerService.errorHandler(error);
      })
    );
  }

  /**
   * Utility method to handle parallel requests
   * E.g. Let's say 5 requests are sent and return 401,
   * then 1 refreshToken is performed, and 5 requests again
   * @returns
   */
  private refreshToken(request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((userProfile) => userProfile != null),
        take(1),
        switchMap(() => {
          return next(this.addAuthHeader(request));
        })
      );
    } else {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      return this.authService.invokeRefreshToken().pipe(
        retry({
          count: this.authContents.retryCount,
          delay: this.authContents.delay,
        }),
        switchMap(() => {
          this.refreshTokenInProgress = false;
          const userProfile = this.authService.getUserProfile();
          this.refreshTokenSubject.next(userProfile);
          request = this.addAuthHeader(request);
          return next(request);
        }),
        catchError((e) => {
          this.refreshTokenInProgress = false;
          this.navigateToLogin();
          return this.errorHandlerService.errorHandler(e);
        })
      );
    }
  }

  /**
   * method to add auth header
   * @param req http request
   * @returns auth header
   */
  public addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const authToken = this.authService.getAuthToken;
    const customerId = this.authService.getCustomerId();
    if (authToken) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    }
    if (customerId) {
      req = req.clone({
        setHeaders: {
          'hg-customer-code': customerId,
        },
      });
    }
    return req;
  }

  /**
   * Interceptor implementation
   * @param req http request
   * @param next http handler
   * @returns intercept
   */
  public intercept(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    // Handle request
    if (!this.whiteListUrls.includes(req.url)) {
      req = this.addAuthHeader(req);
    }
    // Handle response
    return next(req).pipe(
      retry({
        count: this.authContents.retryCount,
        delay: (error) => {
          // Preventing retry for the following status codes 400, 403, 404, 409 — none of these are transient,
          // and retrying a 409 conflict on a non-idempotent POST/PUT would just repeat the same failure
          // (or, worse, double up a create request).
          if (error instanceof HttpErrorResponse && [ErrorHandlerCodesEnum.BAD_REQUEST, ErrorHandlerCodesEnum.FORBIDDEN, ErrorHandlerCodesEnum.NOT_FOUND, ErrorHandlerCodesEnum.CONFLICT].includes(error.status)) {
            return throwError(() => error);
          }
          return timer(this.authContents.delay);
        },
      }),
      catchError((error) => this.handleResponseError(error, req, next))
    );
  }
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const interceptor = inject(AuthInterceptor);
  return interceptor.intercept(req, next);
};
